/**
 * The roster, and the sync that owns it.
 *
 * Read-only on purpose: there is no import endpoint, because the roster is read from EMS and the
 * app's only writes are the sync's. "Who is employed" therefore has one answer rather than two
 * that disagree.
 *
 * The sync panel shows both timestamps — ours and the aggregator's own `synced_at` — because
 * they answer different questions: when we last looked, and how fresh what we saw was. And a run
 * that **aborted on the 20% shrink guard** is shown prominently: it means the roster is
 * deliberately unchanged, which is the opposite of a failed run needing a retry.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ErrorNote, PageHeader, RosterAge } from '@/components/bits';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { formatDate, formatInstant } from '@/lib/dates';
import type { SyncRun } from '@/lib/types';

export function HrEmployees() {
  const cache = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [department, setDepartment] = useState('');

  const departments = useQuery({ queryKey: ['departments'], queryFn: api.departments });
  const runs = useQuery({ queryKey: ['sync-runs'], queryFn: api.syncRuns });

  const employees = useQuery({
    queryKey: ['employees', search, status, category, department],
    queryFn: () =>
      api.employees({
        ...(search.trim().length >= 2 ? { search: search.trim() } : {}),
        ...(status ? { status } : {}),
        ...(category ? { category } : {}),
        ...(department ? { department_id: Number(department) } : {}),
        limit: 500,
      }),
  });

  const resync = useMutation({
    mutationFn: api.runSync,
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['sync-runs'] });
      await cache.invalidateQueries({ queryKey: ['employees'] });
      await cache.invalidateQueries({ queryKey: ['freshness'] });
      await cache.invalidateQueries({ queryKey: ['budget-departments'] });
    },
  });

  const rows = employees.data?.employees ?? [];
  const counts = useMemo(() => {
    const byStatus = new Map<string, number>();
    for (const row of rows) {
      byStatus.set(row.employment_status, (byStatus.get(row.employment_status) ?? 0) + 1);
    }
    return byStatus;
  }, [rows]);

  const lastRun = runs.data?.runs[0];

  return (
    <>
      <PageHeader
        title="Employees"
        subtitle="Read from EMS. Nothing here is typed in by hand."
        actions={
          <Button variant="primary" busy={resync.isPending} onClick={() => resync.mutate()}>
            Re-sync now
          </Button>
        }
      />

      {resync.error ? (
        <div className="mb-4">
          <ErrorNote error={resync.error} />
        </div>
      ) : null}

      <Card title="The sync" className="mb-6">
        <div className="grid gap-4">
          {runs.data ? <RosterAge freshness={runs.data.freshness} /> : null}

          {lastRun?.status === 'aborted' ? (
            <Alert tone="bad" title={`The last run aborted: ${lastRun.abort_reason ?? 'unknown'}`}>
              <strong>The roster is deliberately unchanged.</strong> The circuit-breaker refuses a
              run that would shrink the roster by more than 20%, because a partial response from the
              aggregator would otherwise mark hundreds of people as having left — and their
              entitlement would vanish with them. Investigate the source before re-running.
            </Alert>
          ) : null}

          {runs.data && runs.data.runs.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <Th>Started</Th>
                  <Th>Source freshness</Th>
                  <Th>Status</Th>
                  <Th numeric>Fetched</Th>
                  <Th numeric>New</Th>
                  <Th numeric>Changed</Th>
                  <Th numeric>Left</Th>
                  <Th numeric>Skipped</Th>
                  <Th numeric>Took</Th>
                </tr>
              </thead>
              <tbody>
                {runs.data.runs.map((run) => (
                  <SyncRunRow key={run.id} run={run} />
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm">No sync has run yet.</p>
          )}
        </div>
      </Card>

      <Card
        title={`${rows.length} employees`}
        action={
          <span className="text-muted-foreground text-xs">
            {[...counts.entries()].map(([key, value]) => `${value} ${key}`).join(' · ')}
          </span>
        }
      >
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          <Input
            aria-label="Search by name or employee number"
            placeholder="Name or number (2+ characters)"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            aria-label="Filter by department"
            value={department}
            onChange={(event) => setDepartment(event.target.value)}
          >
            <option value="">Every department</option>
            {(departments.data?.departments ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name} ({entry.headcount})
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="notice">Serving notice</option>
            <option value="exited">Left</option>
          </Select>
          <Select
            aria-label="Filter by category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="">Staff and Workers</option>
            <option value="staff">Staff</option>
            <option value="worker">Workers</option>
          </Select>
        </div>

        {employees.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : rows.length === 0 ? (
          <EmptyState title="Nobody matches those filters" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>No.</Th>
                <Th>Category</Th>
                <Th>Department</Th>
                <Th>EMS team</Th>
                <Th>Joined</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((employee) => (
                <tr key={employee.id}>
                  <Td>
                    {employee.full_name}
                    {employee.designation ? (
                      <p className="text-muted-foreground text-xs">{employee.designation}</p>
                    ) : null}
                  </Td>
                  <Td className="tabular text-xs">{employee.employee_no}</Td>
                  <Td className="text-xs">{employee.category}</Td>
                  <Td className="text-xs">
                    {employee.department_name ?? (
                      // The 13 unplaced Workers (D-33): left unassigned on purpose, and still
                      // selectable for anyone's namelist.
                      <Badge tone="warn">unassigned</Badge>
                    )}
                  </Td>
                  <Td className="text-xs">{employee.ems_team_label ?? '—'}</Td>
                  <Td className="whitespace-nowrap text-xs">{formatDate(employee.hire_date)}</Td>
                  <Td>
                    {employee.employment_status === 'active' ? (
                      <Badge tone="good">active</Badge>
                    ) : employee.employment_status === 'notice' ? (
                      <Badge
                        tone="warn"
                        title="Serving notice — may attend, brings no entitlement (R-08)"
                      >
                        notice
                      </Badge>
                    ) : (
                      <Badge tone="neutral" title={`Last day ${employee.last_day ?? 'unknown'}`}>
                        left
                      </Badge>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function SyncRunRow({ run }: { run: SyncRun }) {
  const [open, setOpen] = useState(false);
  const tone =
    run.status === 'ok'
      ? 'good'
      : run.status === 'partial'
        ? 'warn'
        : run.status === 'aborted'
          ? 'warn'
          : 'bad';

  return (
    <>
      <tr>
        <Td className="whitespace-nowrap text-xs">{formatInstant(run.started_at)}</Td>
        <Td className="whitespace-nowrap text-xs">{formatInstant(run.source_synced_at)}</Td>
        <Td>
          <Badge tone={tone}>{run.status}</Badge>
          {run.errors.length > 0 ? (
            <button
              type="button"
              className="ml-2 text-xs underline underline-offset-4"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
            >
              {run.errors.length} {run.errors.length === 1 ? 'problem' : 'problems'}
            </button>
          ) : null}
        </Td>
        <Td numeric>{run.rows_fetched}</Td>
        <Td numeric>{run.created_count}</Td>
        <Td numeric>{run.updated_count}</Td>
        <Td numeric>{run.exited_count}</Td>
        <Td numeric>{run.skipped_count}</Td>
        <Td numeric className="text-xs">
          {run.duration_ms === null ? '—' : `${(run.duration_ms / 1000).toFixed(1)}s`}
        </Td>
      </tr>
      {open ? (
        <tr>
          <Td colSpan={9}>
            <ul className="grid gap-1 text-xs">
              {run.errors.map((problem, index) => (
                <li key={index}>
                  <span className="font-medium">{problem.stage}</span> — {problem.message}
                  {problem.advice ? (
                    <span className="text-muted-foreground"> · {problem.advice}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Td>
        </tr>
      ) : null}
    </>
  );
}
